import './App.css'
import { formulas } from './data/formulas';
import { useState, useEffect } from 'react';
import TaskPageItem from './TaskPageItem';
import { useLocation } from "react-router-dom";
import { useNavigate } from 'react-router-dom';

const finalFeedbackMessages = {
  low: [
    'Nastavi pokušavati - svaki zadatak pomaže ti da napreduješ.',
    'Dobro je što vježbaš. Greške su dio učenja.',
    'Polako gradiš razumijevanje, nastavi dalje.',
  ],
  medium: [
    'Odličan napredak - nastavi ovim tempom.',
    'Sve si bliže potpunom razumijevanju.',
    'Dobro rješavaš zadatke, samo nastavi vježbati.',
  ],
  high: [
    'Odlično! Pokazuješ vrlo dobro razumijevanje zadataka.',
    'Izvrsno rješavaš zadatke - nastavi tako.',
    'Bravo! Vrlo precizno koristiš matematičke formule.',
  ]
};

function getRandom(arr: string[]) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function getFinalMessage(score: number, total: number) {
  const ratio = score / total;

  if (ratio <= 0.4) return getRandom(finalFeedbackMessages.low);
  if (ratio <= 0.8) return getRandom(finalFeedbackMessages.medium);
  return getRandom(finalFeedbackMessages.high);
}

export default function TaskPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const selectedCategories = location.state?.selectedCategories ?? [];
  const filteredFormulas = selectedCategories.length > 0 ? formulas.filter(f => selectedCategories.includes(f.category)) : formulas;
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});

  const i = index % filteredFormulas.length;
  const formula = filteredFormulas[i];

  const total = filteredFormulas.length;
  
  const taskType = location.state?.taskType ?? 'A';

  const toPrev = () => {if (index > 0) { setIndex(index -1);}};

  const toNext = () => { 
    const nextUnsolved = getNextUnsolvedIndex(index);

    if (nextUnsolved !== -1) {
      setIndex(nextUnsolved);
    }
  };

  
  const getNextUnsolvedIndex = (from: number) => {
    for (let offset = 1; offset <= total; offset++) {
      const next = (from + offset) % total;
      if (!completedTasks.has(next)) {
        return next;
      }
    }
    return -1;
  };

  const [score, setScore] = useState(0);
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());
  const allTasksCompleted = completedTasks.size === total && total > 0;
  const remainingUnsolvedIncludingCurrent = filteredFormulas.filter((_, idx) => !completedTasks.has(idx)).length;

  useEffect(() => {
    if (allTasksCompleted) {
      setFinalMessage(getFinalMessage(score, total)!);
    }
  }, [allTasksCompleted, score, total]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'TEXTAREA' || 
      target.tagName === 'INPUT' ||
      target.isContentEditable
    ) {
      return;
    }
    if (e.key === 'ArrowRight') toNext();
    if (e.key === 'ArrowLeft') toPrev();
  };

  const [finalMessage, setFinalMessage] = useState('');

  if (allTasksCompleted) {
    return (
      <div className="App" >
        <h1 tabIndex = {0} >Svi zadaci su riješeni</h1>

        <p tabIndex = {0}>
          Ostvareni broj bodova: {score} od ukupno {total}
        </p>

        <p
          tabIndex={0}
          role="status"
          aria-live="polite"
        >
          {finalMessage}
        </p>

        <button onClick={() => navigate('/')}>
          Povratak na početni zaslon
        </button>
      </div>
    );
  }

  if (!formula) return <p>Nema zadataka</p>;

  return (
    <div onKeyDown={handleKeyDown} tabIndex={0}>
      <nav aria-live="polite" role="status">
        <span>
          Zadatak {i + 1} od {total}: <strong>{formula?.name}</strong>
        </span>
      </nav>
      {!completedTasks.has(i) && remainingUnsolvedIncludingCurrent === 1 && (
        <div
          role="status"
          aria-live="polite"
          className="last-task-info"
          tabIndex={0}
        >
          Bravo! Ovo je posljednji zadatak u ovoj vježbi. Nakon rješavanja prikazat će se ostvareni rezultat.
        </div>
      )}
      <TaskPageItem
        formula={formula}
        key={formula.latex}
        taskType={taskType}
        score={score}
        isCompleted={completedTasks.has(i)}
        onComplete={(correct: boolean) => {

          if (!completedTasks.has(i)) {
            setCompletedTasks(prev => {
              const next = new Set(prev);
              next.add(i);
              return next;
            });
            if (correct) {
              setScore(sc => sc + 1);
            }
          }
        }} 
        savedAnswer={answers[i] ?? ''}
        onSaveAnswer={(value) => {
          setAnswers(prev => ({
            ...prev,
            [i]: value
          }));
        }}     
      >
      </TaskPageItem>
      <div
        className='div-task-navigation'>
        <button
          aria-label="Prethodni zadatak"
          className='button-task-navigation'
          onClick={toPrev}
          disabled={index === 0}
        >
          Prethodni zadatak
        </button>
        <button
          aria-label="Sljedeći zadatak"
          className='button-task-navigation'
          onClick={toNext}
          disabled={total === 0}
        >
          Sljedeći zadatak
        </button>
      </div>
      <button onClick={() => navigate('/', {})}>
        Povratak na početni zaslon
      </button>
    </div>
  )
}